import { useId, useRef, useState, type DragEvent } from 'react';
import { Button, Text } from 'frosted-ui';
import { ArrowUpFromBracket16, Photo16, Trash16 } from '@frosted-ui/icons';

/** "2.4 MB" / "830 KB" — what the picker shows beside the file name. */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

interface ImageFilePickerProps {
  label: string;
  hint: string;
  accept: string[];
  file: File | null;
  error: string | null;
  onPick: (file: File | null) => void;
}

/** Touch-first devices (phones, tablets) can't drag a file in. */
function isTouchDevice(): boolean {
  return typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches);
}

/**
 * A styled replacement for the native file input: a drop zone with a
 * "Choose image" button (the native input stays in the DOM, hidden, and the
 * button opens it). Shows the picked file's name and size.
 */
export default function ImageFilePicker({ label, hint, accept, file, error, onPick }: ImageFilePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [touch] = useState(isTouchDevice);
  const hintId = useId();
  const errorId = useId();

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onPick(dropped);
  }

  const describedBy = error ? `${hintId} ${errorId}` : hintId;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', minWidth: 0 }}>
      <Text size="2" weight="medium">{label}</Text>
      <div
        data-testid="image-drop-zone"
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-5) var(--space-4)',
          borderRadius: 'var(--radius-4)',
          border: `1px dashed ${error ? 'var(--red-a8)' : dragging ? 'var(--accent-9)' : 'var(--gray-a7)'}`,
          backgroundColor: dragging ? 'var(--accent-a3)' : 'var(--gray-a2)',
          textAlign: 'center',
          transition: 'background-color 120ms ease, border-color 120ms ease',
        }}
      >
        {file ? (
          <>
            <Photo16 aria-hidden style={{ width: 20, height: 20, color: 'var(--gray-11)' }} />
            <Text size="2" weight="medium" style={{ maxWidth: '100%', overflowWrap: 'anywhere' }}>
              {file.name}
            </Text>
            <Text size="1" color="gray">{formatFileSize(file.size)}</Text>
          </>
        ) : (
          <>
            <ArrowUpFromBracket16 aria-hidden style={{ width: 20, height: 20, color: 'var(--gray-11)' }} />
            {!touch && <Text size="2" color="gray">Drag an image here, or</Text>}
          </>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            type="button"
            variant="soft"
            size="2"
            aria-label={file ? `Replace ${label.toLowerCase()}` : `Choose ${label.toLowerCase()}`}
            aria-describedby={describedBy}
            onClick={() => inputRef.current?.click()}
          >
            {file ? 'Replace' : touch ? 'Choose an image' : 'Choose image'}
          </Button>
          {file && (
            <Button type="button" variant="ghost" color="gray" size="2" onClick={() => onPick(null)}>
              <Trash16 /> Remove
            </Button>
          )}
        </div>
        <Text id={hintId} size="1" color="gray">{hint}</Text>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept.join(',')}
        tabIndex={-1}
        aria-hidden
        data-testid="image-file-input"
        style={{ display: 'none' }}
        onChange={(e) => {
          // A cancelled dialog yields no file; keep the current one.
          const picked = e.target.files?.[0];
          if (picked) onPick(picked);
          // Let the same file be picked again after a removal.
          e.target.value = '';
        }}
      />
      {error && (
        <Text id={errorId} size="2" color="red" role="alert">{error}</Text>
      )}
    </div>
  );
}
